<template>
  <div class="sa-card sf-card">
    <div class="sa-card-header">
      <span class="sa-card-title">Funil de Status</span>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="sf-loading">
      <b-spinner small variant="primary"></b-spinner>
    </div>

    <!-- Funnel -->
    <template v-else-if="funnelLevels.length">
      <div class="sf-funnel-wrap">
        <vue-funnel-graph
          :width="funnelWidth"
          :height="funnelHeight"
          :labels="funnelLabels"
          :values="funnelValues"
          :colors="funnelColors"
          :animated="true"
          :display-percentage="false"
          direction="horizontal"
        />

        <!-- Labels personalizados com badge ao lado do número -->
        <div class="sf-label-overlay">
          <div v-for="l in funnelLevels" :key="l.key" class="sf-label-col">
            <div class="sf-label-value-row">
              <span class="sf-label-count">{{ l.count }}</span>
              <span class="sf-label-badge">{{ getPctOfTotal(l.count) }}%</span>
            </div>
            <div class="sf-label-name">{{ l.label }}</div>
          </div>
        </div>
      </div>

      <!-- Waiting list -->
      <div v-if="data.has_waiting_list" class="sf-waiting-list">
        <i class="bx bx-list-ul"></i>
        <span>Lista de espera: <strong>{{ data.waiting_list_count }}</strong></span>
      </div>
    </template>

    <!-- Empty -->
    <div v-else-if="!loading" class="empty-state-card">
      <div class="empty-icon"><i class="bx bx-filter"></i></div>
      <div class="empty-title">Sem dados de status</div>
    </div>
  </div>
</template>

<script>
import { VueFunnelGraph } from 'vue-funnel-graph-js'
import { getStatusFunnel } from '../services'

export default {
  name: 'StatusFunnel',
  components: { VueFunnelGraph },
  props: {
    period: { type: String, default: 'all' },
    dateFrom: { type: String, default: null },
    dateTo: { type: String, default: null }
  },
  data () {
    return {
      loading: false,
      data: null,
      funnelWidth: 260,
      funnelHeight: 220
    }
  },
  computed: {
    funnelLevels () {
      if (!this.data || !this.data.levels) return []
      return this.data.levels.filter(l => l.key !== 'total')
    },
    funnelLabels () {
      return this.funnelLevels.map(l => l.label)
    },
    funnelValues () {
      return this.funnelLevels.map(l => l.count)
    },
    funnelColors () {
      return ['#6366f1', '#556ee6', '#34c38f']
    }
  },
  watch: {
    period () { this.loadData() },
    dateFrom () { this.loadData() },
    dateTo () { this.loadData() }
  },
  mounted () {
    this.loadData()
    this.$nextTick(this.calcSize)
    window.addEventListener('resize', this.calcSize)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.calcSize)
  },
  methods: {
    async loadData () {
      this.loading = true
      try {
        const params = {}
        if (this.dateFrom && this.dateTo) {
          params.date_from = this.dateFrom
          params.date_to = this.dateTo
        } else {
          params.period = this.period === 'custom' ? 'all' : this.period
        }
        const { data } = await getStatusFunnel(params)
        this.data = data
      } catch (e) {
        console.error('Erro ao carregar funil:', e)
      } finally {
        this.loading = false
      }
    },

    calcSize () {
      const el = this.$el
      if (!el) return
      const w = el.clientWidth - 40
      if (w > 0) this.funnelWidth = w
    },

    getPctOfTotal (count) {
      const total = this.data?.total_inscritos || 1
      if (!total) return '0.0'
      return ((count / total) * 100).toFixed(1)
    },

    formatNumber (num) {
      if (num === null || num === undefined) return '0'
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
      return num.toString()
    }
  }
}
</script>

<style scoped>
.sf-card { overflow: hidden; }

.sa-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
  display: flex;
  align-items: center;
}

.sf-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.sf-funnel-wrap {
  position: relative;
  margin-top: 16px;
}

/* Labels overlay — sobre a área de labels da lib */
.sf-label-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  pointer-events: none;
}
.sf-label-col {
  flex: 1 1 0;
  padding-left: 24px;
}
.sf-label-value-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.sf-label-count {
  font-size: 18px;
  font-weight: 700;
  color: #1a1d26;
  line-height: 1;
}
.sf-label-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(85, 110, 230, 0.1);
  color: #556ee6;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  line-height: 1.4;
}
.sf-label-name {
  font-size: 12px;
  font-weight: 600;
  color: #4a5068;
  margin-top: 4px;
}

/* Override funnel lib */
.sf-card :deep(.funnel-graph) { width: 100% !important; }
.sf-card :deep(.svg-funnel-js) { width: 100% !important; }

/* Waiting list */
.sf-waiting-list {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 10px 14px;
  background: #f8f9fc;
  border-radius: 6px;
  font-size: 13px;
  color: #4a5068;
}
.sf-waiting-list i { font-size: 18px; color: #556ee6; }

/* Empty state */
.empty-state-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  text-align: center;
}

.empty-state-card .empty-icon {
  width: 48px;
  height: 48px;
  background: #f5f6fa;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  color: #b0b5c8;
  font-size: 22px;
}

.empty-title {
  font-size: 13px;
  font-weight: 600;
  color: #4a5068;
  margin-bottom: 4px;
}
</style>

<!-- Esconde labels originais da lib (não pode ser scoped) -->
<style>
.sf-card .svg-funnel-js .label__value,
.sf-card .svg-funnel-js .label__title,
.sf-card .svg-funnel-js .label__percentage {
  display: none !important;
}
.sf-card .svg-funnel-js:not(.svg-funnel-js--vertical) .svg-funnel-js__label:not(:first-child) {
  border-left-color: #e8eaf2 !important;
}
</style>
